import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Edit,
  Trash2,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Eye,
  Trash2Icon,
  MoreHorizontalIcon,
  AlertTriangle,
  Loader,
} from "lucide-react";
import MainLayout from "../../../components/layout/MainLayout";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  deleteMaterialListWithProjectService,
  getMaterialListByUserId,
} from "../../../../Services/materialListWithProjectService";
import { t } from "../../../../utils/translate";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "../../projects/components/DeleteConfirmationModal";

export default function MaterialList() {
  const adminId = useSelector((state) => state?.auth?.loggedInUser?._id);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState({
    key: "projectName",
    direction: "ascending",
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Add loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const [modalOpen, setModalOpen] = useState(false);
  const [materialListToDelete, setMaterialListToDelete] = useState(null);

  const getMaterialListData = async () => {
    setIsLoading(true);
    setIsError(false);

    try {
      const responseData = await getMaterialListByUserId(adminId);
      const filterData = responseData?.data.filter((ele) => !ele.isDeleted);
      setData(filterData || []);
    } catch (error) {
      console.error("Error fetching material list:", error);
      setIsError(true);
      setErrorMessage(
        error?.response?.data?.message ||
          "Unable to load material lists. Please check your internet connection."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (adminId) {
      getMaterialListData();
    }
  }, [adminId]);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleDropdown = (id, e) => {
    if (e) e.stopPropagation();
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  useEffect(() => {
    let result = [...data];

    if (searchTerm) {
      result = result.filter(
        (item) =>
          (item.projectName &&
            item.projectName.toLowerCase().includes(searchTerm)) ||
          (item.customerName &&
            item.customerName.toLowerCase().includes(searchTerm)) ||
          (item.materialListName &&
            item.materialListName.toLowerCase().includes(searchTerm)) ||
          (item.comments && item.comments.toLowerCase().includes(searchTerm)) ||
          (item.materialList &&
            item.materialList.some(
              (material) =>
                (material.description &&
                  material.description.toLowerCase().includes(searchTerm)) ||
                (material.product_no &&
                  material.product_no.toLowerCase().includes(searchTerm)) ||
                (material.category &&
                  material.category.toLowerCase().includes(searchTerm))
            ))
      );
    }

    if (statusFilter !== "all") {
      result = result.filter((item) => item.status === statusFilter);
    }

    if (sortConfig.key) {
      result.sort((a, b) => {
        // Handle potentially undefined values
        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";

        if (aValue < bValue) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredData(result);
  }, [data, searchTerm, sortConfig, statusFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest(".dropdown-menu")) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [activeDropdown]);

  const handleDelete = async (id) => {
    setIsDeleting(true);
    try {
      const response = await deleteMaterialListWithProjectService(id);
      if (response?.data?.status === "success") {
        await getMaterialListData();
        toast.success(t("deletedSuccessfully"));
      } else {
        toast.error(t("thereIsSomeError"));
      }
    } catch (error) {
      console.error("Error deleting material list:", error);
      toast.error(
        error?.response?.data?.message ||
          "Unable to delete. Please check your internet connection."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDelete = (materialList) => {
    setMaterialListToDelete(materialList);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (materialListToDelete) {
      handleDelete(materialListToDelete._id);
      setModalOpen(false);
      setMaterialListToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setModalOpen(false);
    setMaterialListToDelete(null);
  };

  const handleRetry = () => {
    getMaterialListData();
  };

  // Error UI component
  const ErrorDisplay = () => (
    <div className="bg-white rounded-lg shadow p-8 text-center">
      <div className="flex justify-center mb-4">
        <AlertTriangle size={48} className="text-red-500" />
      </div>
      <h3 className="text-xl font-bold text-red-600 mb-2">
        Error Loading Data
      </h3>
      <p className="text-gray-700 mb-4">
        {errorMessage || "There was a problem loading material lists."}
      </p>
      <p className="text-gray-600 mb-6">
        Please check your internet connection and try again.
      </p>
      <button
        onClick={handleRetry}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500 flex items-center justify-center mx-auto"
      >
        <RefreshCw size={18} className="mr-2" /> Try Again
      </button>
    </div>
  );

  // Loading UI component
  const LoadingDisplay = () => (
    <div className="bg-white rounded-lg shadow p-8 text-center">
      <div className="flex justify-center mb-4">
        <Loader size={48} className="text-blue-500 animate-spin" />
      </div>
      <h3 className="text-xl font-bold text-blue-600 mb-2">
        Loading Material Lists
      </h3>
      <p className="text-gray-700">Please wait while we fetch your data...</p>
    </div>
  );

  // Empty state component
  const EmptyState = () => (
    <div className="bg-white rounded-lg shadow p-8 text-center">
      <div className="flex justify-center mb-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-16 w-16 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
      </div>
      <h3 className="text-xl font-bold text-gray-700 mb-2">
        No Material Lists Found
      </h3>
      <p className="text-gray-600 mb-6">
        Start by creating your first material list.
      </p>
      <Link to="/admin/material-list/create-material-list-page">
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500">
          + Create Material List
        </button>
      </Link>
    </div>
  );

  return (
    <MainLayout title={"Material Lists"}>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold mb-6">Material Lists</h1>
          <div>
            <Link to="/admin/material-list/create-material-list-page">
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 border border-gray-300 rounded-md hover:bg-blue-500 text-white">
                + Create Material
              </button>
            </Link>
          </div>
        </div>

        {!isLoading && !isError && (
          <div className="flex flex-col md:flex-row justify-between mb-6 gap-4">
            <div className="relative w-full md:w-1/2">
              <input
                type="text"
                placeholder="Search materials, projects, customers..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={handleSearch}
              />
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
            </div>

            <div className="flex gap-2">
              <div className="relative">
                <button
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Filter size={18} />
                  <span>Filter</span>
                </button>

                {isFilterOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                    <div className="p-2 border-b border-gray-200 font-medium">
                      Status
                    </div>
                    <div className="p-2">
                      <label className="flex items-center gap-2 cursor-pointer mb-2">
                        <input
                          type="radio"
                          name="status"
                          checked={statusFilter === "all"}
                          onChange={() => handleStatusFilter("all")}
                          className="form-radio text-blue-500"
                        />
                        <span>All</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer mb-2">
                        <input
                          type="radio"
                          name="status"
                          checked={statusFilter === "latest"}
                          onChange={() => handleStatusFilter("latest")}
                          className="form-radio text-blue-500"
                        />
                        <span>Latest</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer mb-2">
                        <input
                          type="radio"
                          name="status"
                          checked={statusFilter === "under"}
                          onChange={() => handleStatusFilter("under")}
                          className="form-radio text-blue-500"
                        />
                        <span>Under Progress</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="status"
                          checked={statusFilter === "closed"}
                          onChange={() => handleStatusFilter("closed")}
                          className="form-radio text-blue-500"
                        />
                        <span>Closed</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              <select
                className="px-4 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
              </select>
            </div>
          </div>
        )}

        {/* Conditional rendering based on loading and error states */}
        {isLoading ? (
          <LoadingDisplay />
        ) : isError ? (
          <ErrorDisplay />
        ) : filteredData.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="overflow-x-auto bg-white rounded-lg shadow">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10"></th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("projectName")}
                    >
                      <div className="flex items-center gap-2">
                        Project Name
                        {sortConfig.key === "projectName" &&
                          (sortConfig.direction === "ascending" ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          ))}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("materialListName")}
                    >
                      <div className="flex items-center gap-2">
                        Material List Name
                        {sortConfig.key === "materialListName" &&
                          (sortConfig.direction === "ascending" ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          ))}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("customerName")}
                    >
                      <div className="flex items-center gap-2">
                        Customer
                        {sortConfig.key === "customerName" &&
                          (sortConfig.direction === "ascending" ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          ))}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("totalWeight")}
                    >
                      <div className="flex items-center gap-2">
                        Total Weight
                        {sortConfig.key === "totalWeight" &&
                          (sortConfig.direction === "ascending" ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          ))}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("status")}
                    >
                      <div className="flex items-center gap-2">
                        Status
                        {sortConfig.key === "status" &&
                          (sortConfig.direction === "ascending" ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          ))}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort("createdAt")}
                    >
                      <div className="flex items-center gap-2">
                        Created At
                        {sortConfig.key === "createdAt" &&
                          (sortConfig.direction === "ascending" ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          ))}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.map((item) => (
                    <React.Fragment key={item._id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => toggleRowExpansion(item._id)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            {expandedRows[item._id] ? (
                              <ChevronDown size={20} />
                            ) : (
                              <ArrowRight size={20} />
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {item.projectName || "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {item.materialListName || "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {item.customerName || "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {item.totalWeight
                              ? `${item.totalWeight} kg`
                              : "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${
                            item.status === "latest"
                              ? "bg-blue-100 text-blue-800"
                              : item.status === "under"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-green-100 text-green-800"
                          }`}
                          >
                            {item.status === "latest"
                              ? "Latest"
                              : item.status === "under"
                              ? "Under Progress"
                              : "Closed"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.createdAt ? formatDate(item.createdAt) : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium relative">
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/admin/material-list/material-list-detail-page/${item?._id}`}
                            >
                              <button
                                className="text-blue-600 hover:text-blue-900"
                                aria-label="View observation"
                              >
                                <Eye className="h-5 w-5" />
                              </button>
                            </Link>

                            <Link
                              to={`/admin/material-list/edit-material-list-page/${
                                item?.projectId?._id || item?.projectId
                              }/${item?._id}`}
                            >
                              <button
                                className="text-amber-600 hover:text-amber-900"
                                aria-label="Edit observation"
                              >
                                <Edit className="h-5 w-5" />
                              </button>
                            </Link>

                            <button
                              onClick={() => confirmDelete(item)}
                              className="text-red-600 hover:text-red-900"
                              aria-label="Delete observation"
                              disabled={isDeleting}
                            >
                              <Trash2Icon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={(e) => toggleDropdown(item._id, e)}
                              className="text-gray-600 hover:text-gray-900 md:hidden dropdown-menu"
                              aria-label="More options"
                            >
                              <MoreHorizontalIcon className="h-5 w-5" />
                            </button>

                            {activeDropdown === item._id && (
                              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 top-8 dropdown-menu">
                                <Link
                                  to={`/admin/material-list/material-list-detail-page/${item?._id}`}
                                >
                                  <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                    View Details
                                  </button>
                                </Link>

                                <Link
                                  to={`/admin/material-list/edit-material-list-page/${
                                    item?.projectId?._id || item?.projectId
                                  }/${item?._id}`}
                                >
                                  <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                    Edit Observation
                                  </button>
                                </Link>

                                <button
                                  onClick={() => confirmDelete(item)}
                                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                                  disabled={isDeleting}
                                >
                                  Delete Observation
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Row */}
                      {expandedRows[item._id] && (
                        <tr>
                          <td colSpan={8} className="px-6 py-4 bg-gray-50">
                            <div className="text-sm text-gray-900 mb-4">
                              <strong>Comments:</strong>{" "}
                              {item.comments || "No comments"}
                            </div>

                            <div className="text-sm font-medium text-gray-900 mb-2">
                              Material List Items:
                            </div>
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-md">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Product No.
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Description
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Category
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Quantity
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Weight (kg)
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Date
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {item.materialList &&
                                  item.materialList.length > 0 ? (
                                    item.materialList.map((material, index) => (
                                      <tr
                                        key={material._id || index}
                                        className="hover:bg-gray-50"
                                      >
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                          {material.product_no || "N/A"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                          {material.description || "N/A"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                          {material.category || "N/A"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                          {material.quantity || "N/A"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                          {material.kg || "N/A"}
                                        </td>
                                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                          {material.date || "N/A"}
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td
                                        colSpan={6}
                                        className="px-4 py-2 text-center text-sm text-gray-500"
                                      >
                                        No material items found
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredData.length > 0 && (
              <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
                <div className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                  <span className="font-medium">
                    {Math.min(indexOfLastItem, filteredData.length)}
                  </span>{" "}
                  of <span className="font-medium">{filteredData.length}</span>{" "}
                  results
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md ${
                      currentPage === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <ArrowLeft size={16} />
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (number) => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`px-3 py-1 rounded-md ${
                          currentPage === number
                            ? "bg-blue-500 text-white"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        {number}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-md ${
                      currentPage === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={modalOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          projectName={
            materialListToDelete?.materialListName ||
            materialListToDelete?.customerName
          }
          isLoading={isDeleting}
        />

        {/* Add Offline Detection */}
        <OfflineDetector
          onOffline={() => {
            if (!isError) {
              setIsError(true);
              setErrorMessage(
                "You are currently offline. Please check your internet connection."
              );
            }
          }}
        />
      </div>
    </MainLayout>
  );
}

// Offline detector component to monitor network status
const OfflineDetector = ({ onOffline }) => {
  useEffect(() => {
    const handleOffline = () => {
      onOffline();
      toast.error(
        "You are currently offline. Please check your internet connection."
      );
    };

    const handleOnline = () => {
      toast.success("You're back online!");
      window.location.reload();
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [onOffline]);

  return null;
};
